import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { logError } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = n => `$${Number(n||0).toLocaleString('en-US',{minimumFractionDigits:0})}`;

const STATUS_STYLES = {
  paid:     'bg-green-100 text-green-700',
  partial:  'bg-yellow-100 text-yellow-700',
  upcoming: 'bg-blue-100 text-blue-700',
  overdue:  'bg-red-100 text-red-700',
};

export default function QuarterlyTax() {
  const { t } = useTranslation();
  const [data, setData]         = useState(null);
  const [showModal, setModal]   = useState(false);
  const [selQ, setSelQ]         = useState(null);
  const [payForm, setPayForm]   = useState({ amount: '', paid_date: '', payment_method: 'irs_direct_pay' });
  const [saving, setSaving]     = useState(false);
  const year = new Date().getFullYear();

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/quarterly-tax/dashboard?year=${year}`, { withCredentials: true });
      setData(r.data);
    } catch (e) { logError(e, { context: 'QuarterlyTax.load' }); }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const logPayment = async () => {
    if (!payForm.amount || !payForm.paid_date) { toast.error(t('quarterly.amount_and_date_required', 'Amount and date are required')); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/quarterly-tax/payments`,
        { quarter: selQ, year, amount_paid: parseFloat(payForm.amount), paid_date: payForm.paid_date, payment_method: payForm.payment_method },
        { withCredentials: true });
      toast.success(t('quarterly.payment_logged', 'Payment logged'));
      setModal(false); setPayForm({ amount: '', paid_date: '', payment_method: 'irs_direct_pay' });
      load();
    } catch (e) { toast.error(t('common.error','Error logging payment')); logError(e, { context: 'QuarterlyTax.logPayment' }); }
    finally { setSaving(false); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6 p-4">
        <div>
          <h1 className="text-3xl font-bold">{t('quarterly.title', 'Quarterly Tax Command Center')}</h1>
          <p className="text-muted-foreground mt-1">{t('quarterly.subtitle', `Track and manage your ${year} quarterly payments.`)}</p>
        </div>

        {data && (
          <>
            {/* Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {[
                { label: t('quarterly.annual_estimate','Annual Estimate'), value: fmt(data.estimated_annual_tax) },
                { label: t('quarterly.total_paid','Total Paid'), value: fmt(data.total_quarterly_paid) },
                { label: t('quarterly.balance_due','Balance Due'), value: fmt(data.balance_due) },
              ].map(({label, value}) => (
                <Card key={label}><CardContent className="pt-4 text-center">
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className="text-2xl font-black text-gray-900 mt-1">{value}</p>
                </CardContent></Card>
              ))}
            </div>

            {/* Safe harbor */}
            {data.safe_harbor_available && (
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="pt-4">
                  <p className="font-semibold text-blue-900">{t('quarterly.safe_harbor_available','Safe Harbor Available')}</p>
                  <p className="text-sm text-blue-700 mt-1">
                    {t('quarterly.safe_harbor_desc', `Pay ${fmt(data.safe_harbor_amount)} total (${fmt(data.safe_harbor_amount/4)}/quarter) to avoid underpayment penalties.`, { amount: fmt(data.safe_harbor_amount), per_quarter: fmt(data.safe_harbor_amount/4) })}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Quarter cards */}
            {(data.quarters || []).map(q => (
              <Card key={q.quarter} data-testid={`quarter-${q.quarter}`}>
                <CardContent className="pt-5">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-gray-900">{q.label}</h3>
                        <Badge className={`text-xs ${STATUS_STYLES[q.status]}`}>{q.status}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{q.period} · Due {q.due_date}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">{t('quarterly.recommended','Recommended')}</p>
                      <p className="text-xl font-black text-gray-900">{fmt(q.recommended_payment)}</p>
                      {q.safe_harbor_payment !== q.recommended_payment && (
                        <p className="text-xs text-blue-600">{t('quarterly.safe_harbor','Safe harbor')}: {fmt(q.safe_harbor_payment)}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-between items-center mt-3">
                    <span className="text-sm text-gray-600">
                      {t('quarterly.paid','Paid')}: <strong>{fmt(q.amount_paid)}</strong>
                      {q.shortfall > 0 && (
                        <span className="text-red-500 ml-2">({t('quarterly.shortfall','shortfall')}: {fmt(q.shortfall)})</span>
                      )}
                    </span>
                    <Button variant="outline" size="sm"
                      onClick={() => { setSelQ(q.quarter); setModal(true); }}
                      data-testid={`log-payment-q${q.quarter}`}>
                      + {t('quarterly.log_payment','Log Payment')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}

            <p className="text-xs text-muted-foreground italic">{data.disclaimer}</p>
          </>
        )}

        {/* Log payment modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-sm">
              <CardHeader>
                <CardTitle>{t('quarterly.log_payment_title', `Log Q${selQ} Payment`)}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <input type="number" placeholder={t('quarterly.amount','Amount ($)')}
                  value={payForm.amount} onChange={e => setPayForm(f=>({...f,amount:e.target.value}))}
                  className="w-full border border-gray-200 rounded px-3 py-2 text-sm" />
                <input type="date" value={payForm.paid_date}
                  onChange={e => setPayForm(f=>({...f,paid_date:e.target.value}))}
                  className="w-full border border-gray-200 rounded px-3 py-2 text-sm" />
                <select value={payForm.payment_method}
                  onChange={e => setPayForm(f=>({...f,payment_method:e.target.value}))}
                  className="w-full border border-gray-200 rounded px-3 py-2 text-sm">
                  {['irs_direct_pay','check','eft','other'].map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                <div className="flex gap-2 pt-1">
                  <Button onClick={logPayment} disabled={saving} className="flex-1" data-testid="confirm-log-payment">
                    {saving ? t('common.saving','Saving…') : t('common.save','Save')}
                  </Button>
                  <Button variant="outline" onClick={() => setModal(false)} className="flex-1">
                    {t('common.cancel','Cancel')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
